import React, { createContext, useState, useEffect, useContext } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/profile';
import { deleteAvatarFromStorage, deleteOldAvatarAndUploadNew } from '@/utils/avatarUtils';
import { claimSessionPrompt } from '@/utils/firstRun';
import { getMissingProfileFields, REQUIRED_PROFILE_FIELDS, isDobMissing } from '@/utils/profileCompleteness';
import { setErrorReportingAuthToken } from '@/lib/errorReporting';

interface AuthContextType {
  session: Session | null;
  user: Session['user'] | null;
  profile: Profile | null;
  loading: boolean;
  ready: boolean;
  error: string | null;
  isConnected: boolean;
  missingMandatoryFields: string[];
  /** Fields required immediately (blocking); everything else is deferrable. */
  missingRequiredFields: string[];
  showMandatoryFieldsPrompt: boolean;
  setShowMandatoryFieldsPrompt: (show: boolean) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName?: string, ageConfirmedAt?: string, dob?: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Profile) => Promise<void>;
  uploadAvatar: (file: File, currentAvatarUrl?: string | null) => Promise<string>;
  refreshProfile: () => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Fields that block the app on first sign-in. Everything else is deferrable
// and nudged through the home-screen profile completion banner instead.
const REQUIRED_FIELDS = REQUIRED_PROFILE_FIELDS as readonly string[];

// Sentinel-aware check shared with the home screen and Edit Profile.
const checkMissingMandatoryFields = (profileData: any): string[] =>
  getMissingProfileFields(profileData);


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<Session['user'] | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(true);
  const [missingMandatoryFields, setMissingMandatoryFields] = useState<string[]>([]);
  const [missingRequiredFields, setMissingRequiredFields] = useState<string[]>([]);
  const [showMandatoryFieldsPrompt, setShowMandatoryFieldsPrompt] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    const initializeAuth = async () => {
      try {
        console.log('🔐 AuthProvider: Initializing authentication...');

        // Set up auth state listener FIRST (critical for session restoration)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            if (!mounted) return;
            
            console.log('🔄 AuthProvider: Auth state changed:', event, !!session);
            setSession(session);
            setUser(session?.user ?? null);
            setErrorReportingAuthToken(session?.access_token ?? null);
            setError(null);
            setIsConnected(true);
            
            // Mark as ready after initial session or significant auth events
            if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
              setLoading(false);
              // Add a small delay to ensure state is stable
              setTimeout(() => {
                if (mounted) {
                  setReady(true);
                  console.log('✅ AuthProvider: Ready state set to true');
                }
              }, 100);
            }
          }
        );

        // THEN check for existing session with a more patient approach
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('🔥 AuthProvider: Session error:', sessionError);
          if (mounted) {
            setError('Authentication service temporarily unavailable');
            setIsConnected(false);
            setLoading(false);
            setReady(true);
          }
        } else {
          console.log('✅ AuthProvider: Initial session retrieved:', !!session);
          if (mounted) {
            setSession(session);
            setUser(session?.user ?? null);
            setErrorReportingAuthToken(session?.access_token ?? null);
            setIsConnected(true);
            // Don't set loading to false here - let the auth state change handler do it
          }
        }

        return () => {
          mounted = false;
          subscription.unsubscribe();
        };
        
      } catch (error: any) {
        console.error('🔥 AuthProvider: Initialization error:', error);
        if (mounted) {
          const errorMessage = error?.message || 'Failed to initialize authentication';
          console.error('🔥 AuthProvider: Error details:', { 
            message: errorMessage,
            code: error?.code,
            stack: error?.stack 
          });
          setError(`Authentication initialization failed: ${errorMessage}`);
          setIsConnected(false);
          setLoading(false);
          setReady(true);
        }
      }
    };

    const cleanup = initializeAuth();
    
    // Fallback timeout in case auth never responds
    const fallbackTimeout = setTimeout(() => {
      if (mounted && loading) {
        console.warn('⚠️ AuthProvider: Fallback timeout - setting loading to false');
        setLoading(false);
        setReady(true);
      }
    }, 8000); // 8 second absolute maximum

    return () => {
      cleanup.then(cleanupFn => cleanupFn?.());
      clearTimeout(fallbackTimeout);
    };
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        setProfile(null);
        return;
      }

      try {
        console.log('👤 AuthProvider: Fetching profile for user:', user.id);
        const [{ data: profileData, error }, { data: sensitiveData }] = await Promise.all([
          supabase.from('profiles').select('*').eq('id', user.id).single(),
          supabase.rpc('get_my_sensitive_profile_fields'),
        ]);

        if (error) {
          console.error('🔥 AuthProvider: Profile fetch error:', error);
          // Don't set error state for missing profile, just log it
          if (error.code !== 'PGRST116') {
            setError('Failed to load user profile');
          }
        } else {
          console.log('✅ AuthProvider: Profile loaded successfully');
          const sensitive = Array.isArray(sensitiveData) && sensitiveData.length > 0 ? sensitiveData[0] : {};
          const merged = { ...(profileData as any), ...sensitive } as Profile;
          setProfile(merged);

          // GDPR: persist the age declaration captured at signup, once a session exists.
          // The database trigger normally stores these already; this is a fallback for
          // accounts created before the trigger change or when metadata was missing.
          try {
            const pendingAgeConfirm = localStorage.getItem('luvall-age-confirmed-at');
            const pendingDob = localStorage.getItem('luvall-dob');
            const updates: Record<string, any> = {};
            if (pendingAgeConfirm && !(merged as any).age_confirmed_at) {
              updates.age_confirmed_at = pendingAgeConfirm;
            }
            if (pendingDob && isDobMissing((merged as any).DOB)) {
              updates.DOB = pendingDob;
            }
            if (Object.keys(updates).length > 0) {
              await supabase.from('profiles').update(updates as any).eq('id', user.id);
            }
            if (pendingAgeConfirm) localStorage.removeItem('luvall-age-confirmed-at');
            if (pendingDob) localStorage.removeItem('luvall-dob');
          } catch (e) {
            console.warn('Could not persist age confirmation', e);
          }
          

          
          // Check for missing mandatory fields
          const missingFields = checkMissingMandatoryFields(merged);
          setMissingMandatoryFields(missingFields);

          // Only blocking (required) fields interrupt the user; deferrable
          // fields are nudged from the home screen banner instead.
          const missingRequired = missingFields.filter((f) => REQUIRED_FIELDS.includes(f));
          setMissingRequiredFields(missingRequired);

          if (missingRequired.length > 0 && claimSessionPrompt('profile')) {
            console.log('⚠️ AuthProvider: Missing required fields:', missingRequired);
            // Delay the prompt slightly to let the UI settle
            setTimeout(() => {
              setShowMandatoryFieldsPrompt(true);
            }, 500);
          }
        }
      } catch (error) {
        console.error('🔥 AuthProvider: Unexpected profile error:', error);
        setError('Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchProfile();
    } else {
      setProfile(null);
    }
  }, [user]);

  const clearError = () => {
    setError(null);
  };

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      
      const { error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });
      
      if (error) {
        console.log('Auth error:', { code: error.code, message: error.message });
        throw error;
      }

      // Wait for the session to be established and update local state
      const { data: { session: newSession } } = await supabase.auth.getSession();
      if (newSession) {
        setSession(newSession);
        setUser(newSession.user);
      }
    } catch (error: any) {
      console.error('Error signing in:', error);
      
      // Use error codes for more reliable error handling
      switch (error.code) {
        case 'invalid_credentials':
          throw new Error('Invalid email or password. Please check your credentials and try again.');
        case 'email_not_confirmed':
          throw new Error('Email not confirmed');
        case 'too_many_requests':
          throw new Error('Too many login attempts. Please wait a moment before trying again.');
        default:
          // Fallback to message checking for any edge cases
          if (error.message === 'Invalid login credentials') {
            throw new Error('Invalid email or password. Please check your credentials and try again.');
          } else if (error.message.includes('Email not confirmed') || error.message.includes('confirm')) {
            throw new Error('Email not confirmed');
          } else if (error.message.includes('Too many requests')) {
            throw new Error('Too many login attempts. Please wait a moment before trying again.');
          } else {
            throw new Error(error.message || 'Failed to sign in. Please try again.');
          }
      }
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    fullName?: string,
    ageConfirmedAt?: string,
    dob?: string,
  ) => {
    try {
      setError(null);
      setLoading(true);

      // Check for referral code in localStorage
      const referralCode = localStorage.getItem('referral_code') || undefined;

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName || '',
            // GDPR: carry the age declaration server-side so it survives
            // verifying the email on another device or browser.
            ...(ageConfirmedAt ? { age_confirmed_at: ageConfirmedAt } : {}),
            ...(dob ? { dob } : {}),
            ...(referralCode ? { referral_code: referralCode } : {}),
          }
        }
      });

      // Clear referral code after use
      if (referralCode) {
        localStorage.removeItem('referral_code');
      }

      if (error) {
        console.log('Signup error:', { code: error.code, message: error.message });
        throw error;
      }
    } catch (error: any) {
      console.error('Error signing up:', error);
      
      // Use error codes for more reliable error handling
      switch (error.code) {
        case 'user_already_exists':
          throw new Error('An account with this email already exists. Please sign in instead.');
        case 'weak_password':
          throw new Error('Password must be at least 6 characters long.');
        case 'invalid_email':
          throw new Error('Please enter a valid email address.');
        default:
          // Fallback to message checking for any edge cases
          if (error.message === 'User already registered') {
            throw new Error('An account with this email already exists. Please sign in instead.');
          } else if (error.message.includes('Password should be at least')) {
            throw new Error('Password must be at least 6 characters long.');
          } else if (error.message.includes('Invalid email')) {
            throw new Error('Please enter a valid email address.');
          } else {
            throw new Error(error.message || 'Failed to create account. Please try again.');
          }
      }
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      
      // Set user offline before signing out, but only if session is valid and matches
      try {
        const { data } = await supabase.auth.getSession();
        const sessionUserId = data.session?.user?.id;
        const currentTime = new Date().toISOString();

        if (sessionUserId && user?.id && sessionUserId === user.id) {
          console.log('🔴 AuthContext: Setting user offline before logout:', sessionUserId);
          const { error } = await supabase
            .from('user_presence')
            .upsert({ 
              user_id: sessionUserId, 
              is_online: false, 
              last_seen: currentTime,
              updated_at: currentTime
            });
          if (error) {
            console.warn('🚫 AuthContext: Failed to set offline before logout (ignored):', error.message);
          }
        } else {
          console.log('🔴 AuthContext: Skipping remote offline update (no/mismatched session)');
        }
      } catch (e) {
        console.warn('🚫 AuthContext: Exception during offline update (ignored):', e);
      }

      await supabase.auth.signOut();
      setProfile(null);
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out. Please try again.');
      throw new Error('Failed to sign out. Please try again.');
    }
  };

  const updateProfile = async (updates: Profile) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (error) throw error;

      // Optimistically update local state
      setProfile(prev => prev ? { ...prev, ...updates } : null);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const uploadAvatar = async (file: File, currentAvatarUrl?: string | null): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    // Validate file size (8MB limit)
    const maxSize = 8 * 1024 * 1024; // 8MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size must be less than 8MB');
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Only JPG, PNG, and GIF files are supported');
    }

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      // Store under a user-scoped folder so storage RLS can enforce ownership by path prefix
      const filePath = `${user.id}/${Date.now()}.${fileExt}`;

      // Upload new avatar (deletion of old avatar is handled within this function)
      const publicUrl = await deleteOldAvatarAndUploadNew(
        currentAvatarUrl,
        file,
        'avatars',
        filePath
      );

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          avatar_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Update local state
      setProfile(prev => prev ? { ...prev, avatar_url: publicUrl } : null);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      throw error;
    }
  };

  const refreshProfile = async () => {
    if (!user?.id) {
      setProfile(null);
      return;
    }

    try {
      const [{ data: profileData, error }, { data: sensitiveData }] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', user.id).single(),
        supabase.rpc('get_my_sensitive_profile_fields'),
      ]);

      if (error) {
        console.error('Error refreshing profile:', error);
      }

      const sensitive = Array.isArray(sensitiveData) && sensitiveData.length > 0 ? sensitiveData[0] : {};
      setProfile({ ...(profileData as any), ...sensitive } as Profile);
    } catch (error) {
      console.error('Unexpected error refreshing profile:', error);
    }
  };

  const resendVerification = async (email: string) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        console.log('Resend verification error:', { code: error.code, message: error.message });
        throw error;
      }
    } catch (error: any) {
      console.error('Error resending verification:', error);
      
      // Use error codes for more reliable error handling
      switch (error.code) {
        case 'too_many_requests':
          throw new Error('Too many requests. Please wait a moment before trying again.');
        default:
          // Fallback to message checking
          if (error.message.includes('too_many_requests')) {
            throw new Error('Too many requests. Please wait a moment before trying again.');
          } else {
            throw new Error(error.message || 'Failed to resend verification email');
          }
      }
    }
  };

  const value: AuthContextType = {
    session,
    user,
    profile,
    loading,
    ready,
    error,
    isConnected,
    missingMandatoryFields,
    missingRequiredFields,
    showMandatoryFieldsPrompt,
    setShowMandatoryFieldsPrompt,
    signIn,
    signUp,
    signOut,
    updateProfile,
    uploadAvatar,
    refreshProfile,
    resendVerification,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
